"use client";

import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import { ReactLenis, useLenis } from "lenis/react";
import { usePathname } from "next/navigation";
import { useEffect, useRef } from "react";

import { isLenisSmoothScrollEnabled } from "@/lib/lenis-config";

gsap.registerPlugin(ScrollTrigger);

function LenisGsapBridge() {
  const lenis = useLenis();

  useGSAP(
    () => {
      if (!lenis) return;

      const onScroll = () => {
        ScrollTrigger.update();
      };
      lenis.on("scroll", onScroll);

      const tick = (time: number) => {
        lenis.raf(time * 1000);
      };
      gsap.ticker.add(tick);
      gsap.ticker.lagSmoothing(0);

      return () => {
        lenis.off("scroll", onScroll);
        gsap.ticker.remove(tick);
        gsap.ticker.lagSmoothing(500, 16);
      };
    },
    { dependencies: [lenis] }
  );

  return null;
}

function LenisRouteScrollReset() {
  const lenis = useLenis();
  const pathname = usePathname();
  const previousPathnameRef = useRef(pathname);

  useEffect(() => {
    // Skip initial mount and only reset on actual route transitions.
    if (previousPathnameRef.current === pathname) return;
    previousPathnameRef.current = pathname;

    lenis?.stop();
    lenis?.scrollTo(0, { immediate: true, force: true });
    window.scrollTo(0, 0);

    const raf = window.requestAnimationFrame(() => {
      lenis?.start();
    });

    return () => window.cancelAnimationFrame(raf);
  }, [lenis, pathname]);

  return null;
}

/**
 * `stopInertiaOnNavigate` only runs on `click` (bubble). During wheel inertia, the browser
 * often delays or drops that click. Reset Lenis on **pointerdown capture** so momentum is
 * cleared before the click-vs-scroll decision runs (same rule as Lenis: same-origin, new path).
 */
function LenisInternalLinkPointerCaptureReset() {
  const lenis = useLenis();

  useEffect(() => {
    if (!lenis) return;

    const onPointerDownCapture = (event: PointerEvent) => {
      if (event.button !== 0) return;

      const linkUrls = event
        .composedPath()
        .filter((node): node is HTMLAnchorElement => node instanceof HTMLAnchorElement && Boolean(node.href))
        .map((a) => new URL(a.href));

      const currentUrl = new URL(window.location.href);
      const navigatesAway = linkUrls.some(
        (targetUrl) =>
          currentUrl.origin === targetUrl.origin &&
          (currentUrl.pathname !== targetUrl.pathname || currentUrl.search !== targetUrl.search),
      );

      if (navigatesAway) {
        // Public API: `immediate` path calls Lenis’s internal `reset()` (clears wheel inertia).
        lenis.scrollTo(lenis.actualScroll, { immediate: true, force: true });
      }
    };

    window.addEventListener("pointerdown", onPointerDownCapture, true);
    return () => window.removeEventListener("pointerdown", onPointerDownCapture, true);
  }, [lenis]);

  return null;
}

type SmoothScrollProviderProps = {
  children: React.ReactNode;
  /**
   * Override env-based toggle (e.g. Storybook or tests).
   */
  enabled?: boolean;
};

export function SmoothScrollProvider({
  children,
  enabled = isLenisSmoothScrollEnabled(),
}: SmoothScrollProviderProps) {
  if (!enabled) {
    return children;
  }

  return (
    <ReactLenis
      root
      options={{
        autoRaf: false,
        /** Without this, wheel momentum can “eat” the first click on in-page links until scrolling settles. */
        stopInertiaOnNavigate: true,
      }}
    >
      <LenisGsapBridge />
      <LenisRouteScrollReset />
      <LenisInternalLinkPointerCaptureReset />
      {children}
    </ReactLenis>
  );
}
